/**
 * CertificateApprovalsPage — iter-217c-v17
 *
 * Admin approval queue for pending certificates.
 *   • Shows every certificate with status="pending_approval" across ALL
 *     categories (merchants + customers + company + employees).
 *   • Each row has an inline preview button and an Approve / Reject
 *     action. Approving snapshots the default signature + stamp into
 *     the record.
 *   • Landing point from: Signatures & Approvals → Certificate approvals.
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Award, Loader2, Clock, CheckCircle2, XCircle, Eye, Store, Users,
  Building2, Briefcase, Settings, PenLine,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import { Button } from '../../components/ui/button';
import DashboardLayout from '../../components/DashboardLayout';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
  merchant: { ar: 'التجار',  en: 'Merchants',  icon: Store,     color: '#8B5CF6' },
  customer: { ar: 'العملاء', en: 'Customers',  icon: Users,     color: '#1E9BD9' },
  company:  { ar: 'شركات',    en: 'Companies',  icon: Building2, color: '#F59E0B' },
  employee: { ar: 'موظفون',  en: 'Employees',  icon: Briefcase, color: '#10b981' },
};

export default function CertificateApprovalsPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { getAuthHeaders: authHeaders } = useAuth();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/admin/certificates/list`,
        { headers: authHeaders(), params: { status: 'pending_approval' } },
      );
      setRows(Array.isArray(data?.data) ? data.data : []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('فشل التحميل', 'Load failed'));
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const approve = async (cert) => {
    setBusy((b) => ({ ...b, [cert.id]: 'approve' }));
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/certificates/${cert.id}/approve`,
        {},
        { headers: authHeaders() },
      );
      toast.success(t('تم الاعتماد بالتوقيع والختم', 'Approved with signature & stamp'));
      setRows((r) => r.filter((x) => x.id !== cert.id));
    } catch (err) {
      const msg = err?.response?.data?.detail || t('فشل الاعتماد', 'Approve failed');
      toast.error(msg);
      if (String(msg).includes('signature') || String(msg).includes('توقيع')) {
        // Point user to settings when the block is "no default signature".
        // iter-218 — settings moved under لوحة التحكم ▸ Employees Settings.
        setTimeout(() => navigate('/control-panel-settings/company-mgmt/employees/signature'), 900);
      }
    } finally {
      setBusy((b) => ({ ...b, [cert.id]: null }));
    }
  };

  const reject = async (cert) => {
    const reason = window.prompt(t('سبب الرفض (اختياري):', 'Rejection reason (optional):'), '');
    if (reason === null) return;   // cancelled
    setBusy((b) => ({ ...b, [cert.id]: 'reject' }));
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/certificates/${cert.id}/reject`,
        { reason },
        { headers: authHeaders() },
      );
      toast.success(t('تم الرفض', 'Rejected'));
      setRows((r) => r.filter((x) => x.id !== cert.id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('فشل الرفض', 'Reject failed'));
    } finally {
      setBusy((b) => ({ ...b, [cert.id]: null }));
    }
  };

  return (
    <DashboardLayout
      title="اعتماد الشهادات"
      titleEn="Certificate Approvals"
      subtitle="راجع الشهادات المعلّقة واعتمدها لختمها بالتوقيع والختم الافتراضي"
      subtitleEn="Review pending certificates — approve to stamp with default signature + stamp"
      screenCode="SIG-CERT-APR"
      backPath="/signatures-approvals"
      onBack={() => navigate('/signatures-approvals')}
      breadcrumb={[
        { labelAr: 'لوحة تحكم الموظف', labelEn: 'Employee Dashboard', path: '/employee-dashboard' },
        { labelAr: 'التواقيع والاعتماد', labelEn: 'Signatures & Approvals', path: '/signatures-approvals' },
        { labelAr: 'اعتماد الشهادات', labelEn: 'Certificate Approvals' },
      ]}
    >
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="cert-approvals-page">
        {/* iter-218k (Feb 2026) — DYNAMIC signer replaces the fixed
             designated-approver concept. Whoever clicks "Approve" gets
             their own saved signature + AR/EN name + AR/EN job title
             stamped on the certificate. No fixed pre-assignment.  */}
        <div className="mb-5 rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.06] p-4"
             data-testid="dynamic-signer-info-card">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center flex-shrink-0">
              <PenLine className="w-4 h-4 text-emerald-300" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-bold text-white mb-1">
                {t('التوقيع ديناميكي حسب المعتمد', 'Dynamic signature per approver')}
              </h3>
              <p className="text-[12px] text-white/70 leading-relaxed">
                {t(
                  'أي شهادة تُعتمَد ستظهر عليها توقيع + اسم + مسمى الموظف الذي ضغط "اعتماد"، من ملفه في "إعدادات الموظفين ▸ التوقيع".',
                  'Any certificate approved will show the signature + name + job title of the admin who clicks "Approve", pulled from their profile at Employees Settings ▸ Signature.',
                )}
              </p>
              <button
                type="button"
                onClick={() => navigate('/manage/cloudgm-fz-llc/settings/signatures/new')}
                className="mt-2 inline-flex items-center gap-1.5 text-[12px] text-emerald-200 hover:text-emerald-100 underline underline-offset-2"
                data-testid="add-my-signature-shortcut"
              >
                <PenLine className="w-3.5 h-3.5" />
                {t('أضف توقيعك من هنا', 'Add your signature here')}
              </button>
            </div>
          </div>
        </div>

        {/* Header with settings shortcut */}
        <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
          <div className="flex items-center gap-2 text-sm text-white/70">
            <Clock className="w-4 h-4 text-amber-300" />
            <span>{t(`${rows.length} شهادة بانتظار الاعتماد`, `${rows.length} certificates pending approval`)}</span>
          </div>
          <Button
            onClick={() => navigate('/control-panel-settings/company-mgmt/employees/signature')}
            variant="outline"
            className="border-white/15 bg-white/[0.03] text-white hover:bg-white/[0.06] rounded-xl h-10"
            data-testid="signature-settings-btn"
          >
            <Settings className="w-4 h-4 me-1.5" />
            {t('إعدادات التوقيع والختم', 'Signature & Stamp settings')}
          </Button>
        </div>

        {loading ? (
          <div className="p-16 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-white/40" /></div>
        ) : rows.length === 0 ? (
          <div className="bg-white/[0.02] border border-dashed border-white/15 rounded-2xl p-14 text-center">
            <CheckCircle2 className="w-10 h-10 text-emerald-300/60 mx-auto mb-3" />
            <div className="text-white/70 text-sm">
              {t('لا توجد شهادات معلّقة للاعتماد حالياً', 'No pending certificates right now')}
            </div>
          </div>
        ) : (
          <ul className="space-y-2" data-testid="cert-pending-rows">
            {rows.map((cert) => {
              const catMeta = CATEGORY_META[cert.category] || CATEGORY_META.customer;
              const CatIcon = catMeta.icon;
              const busyState = busy[cert.id];
              return (
                <motion.li
                  key={cert.id}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white/[0.02] hover:bg-white/[0.04] border border-white/10 hover:border-white/25 rounded-xl p-4"
                  data-testid={`cert-pending-${cert.id}`}
                >
                  <div className="flex items-center gap-3 flex-wrap">
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${catMeta.color}15`, border: `1px solid ${catMeta.color}40` }}
                    >
                      <CatIcon className="w-4 h-4" style={{ color: catMeta.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-semibold text-sm truncate">{cert.recipient_name}</div>
                      <div className="text-[12px] text-white/55 truncate mt-0.5">
                        <span style={{ color: catMeta.color }}>{isAr ? catMeta.ar : catMeta.en}</span>
                        <span className="mx-2 text-white/25">·</span>
                        <span>{(isAr ? cert.subject_ar : cert.subject_en) || cert.subject_ar || '—'}</span>
                        <span className="mx-2 text-white/25">·</span>
                        <span className="text-white/45 font-mono" dir="ltr">{cert.cert_no}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => navigate(`/manage/cloudgm-fz-llc/certificates/${cert.category}/view/${cert.id}`)}
                        className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-white/5 border border-white/10 text-white/80 hover:text-white hover:bg-white/10 text-xs font-medium"
                        title={t('معاينة', 'Preview')}
                        data-testid={`cert-preview-${cert.id}`}
                      >
                        <Eye className="w-3.5 h-3.5" />
                        {t('معاينة', 'Preview')}
                      </button>
                      <button
                        type="button"
                        onClick={() => reject(cert)}
                        disabled={!!busyState}
                        className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-200 hover:bg-rose-500/20 text-xs font-medium disabled:opacity-40"
                        data-testid={`cert-reject-${cert.id}`}
                      >
                        {busyState === 'reject' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />}
                        {t('رفض', 'Reject')}
                      </button>
                      <button
                        type="button"
                        onClick={() => approve(cert)}
                        disabled={!!busyState}
                        className="inline-flex items-center gap-1.5 px-4 h-9 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold disabled:opacity-40"
                        data-testid={`cert-approve-${cert.id}`}
                      >
                        {busyState === 'approve' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                        {t('اعتماد', 'Approve')}
                      </button>
                    </div>
                  </div>
                </motion.li>
              );
            })}
          </ul>
        )}
      </section>
    </DashboardLayout>
  );
}
